import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import useSEO from "../../lib/useSEO";
import { COMPARES, compareBySlug } from "../../data/seoCompare";
import { SeoShell, SeoHero, PlanCTA, Breadcrumb, crumbsLd } from "./SeoLayout";

function CompareTable({ c }) {
  return (
    <div className="overflow-x-auto rounded-md border bg-white" style={{ borderColor: "#F0D3D8" }} data-testid="compare-table">
      <table className="w-full text-sm min-w-[640px]">
        <thead>
          <tr className="text-left text-white" style={{ background: "#4A0F1A" }}>
            <th className="p-4 font-mono text-[11px] uppercase tracking-[0.16em]">Capability</th>
            <th className="p-4">{c.name}</th>
            <th className="p-4" style={{ background: "rgba(201,166,107,0.18)" }}>Capittance</th>
          </tr>
        </thead>
        <tbody>
          {c.rows.map(([cap, them, us], i) => (
            <tr key={cap} className="border-t align-top" style={{ borderColor: "#F0D3D8", background: i % 2 ? "#FDFBFB" : "#FFFFFF" }} data-testid={`compare-row-${i}`}>
              <td className="p-4 font-semibold text-[#0F1723]">{cap}</td>
              <td className="p-4 text-zinc-700">{them}</td>
              <td className="p-4 text-[#0F1723] font-medium" style={{ background: "#FDF9F7" }}>{us}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ComparePage() {
  const { slug } = useParams();
  const c = compareBySlug(slug);
  const crumbs = [["/", "Home"], ["/compare", "Compare"], [`/compare/${slug}`, c ? `vs ${c.name}` : ""]];
  useSEO({
    title: c ? `${c.title} | Capittance` : "Compare | Capittance",
    description: c?.description,
    path: `/compare/${slug}`,
    jsonLd: c && { "@context": "https://schema.org", "@graph": [{ "@type": "WebPage", name: c.title, description: c.description }, crumbsLd(crumbs)] },
  });
  if (!c) return <Navigate to="/compare" replace />;
  const others = COMPARES.filter((o) => o.slug !== c.slug);
  return (
    <SeoShell>
      <SeoHero eyebrow="Head-to-head comparison" h1={`Capittance vs ${c.name}`} intro={c.summary} />
      <main className="max-w-6xl mx-auto px-6 py-12" data-testid={`compare-${c.slug}`}>
        <Breadcrumb items={crumbs} />
        <CompareTable c={c} />
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <div className="p-6 rounded-md bg-white border" style={{ borderColor: "#F0D3D8" }}>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{c.name} is best for</div>
            <p className="mt-2 text-[15px] text-zinc-800">{c.bestFor}</p>
          </div>
          <div className="p-6 rounded-md bg-white border" style={{ borderColor: "#C9A66B" }}>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: "#7A1F2B" }}>Capittance is best for</div>
            <p className="mt-2 text-[15px] text-zinc-800">ERP and transformation programmes that need testing, cutover, hypercare and executive reporting in one live view.</p>
          </div>
        </div>
        <p className="mt-6 text-xs text-zinc-500">Competitor details are summarised from public information and may change. Check the vendor's site for current features and pricing.</p>
        <PlanCTA id="compare" />
        <div className="mt-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">More comparisons</div>
          <div className="flex flex-wrap gap-2">
            {others.map((o) => <Link key={o.slug} to={`/compare/${o.slug}`} className="px-3 py-2 rounded-sm border bg-white text-sm hover:border-zinc-500 transition-colors" style={{ borderColor: "#F0D3D8" }} data-testid={`compare-more-${o.slug}`}>vs {o.name}</Link>)}
            <Link to="/vs/microsoft-project" className="px-3 py-2 rounded-sm border bg-white text-sm" style={{ borderColor: "#F0D3D8" }} data-testid="compare-more-microsoft-project">vs Microsoft Project</Link>
          </div>
        </div>
      </main>
    </SeoShell>
  );
}

export function CompareHub() {
  useSEO({
    title: "Capittance Alternatives & Comparisons: Jira, Smartsheet, Excel, SAP Cloud ALM | Capittance",
    description: "Compare Capittance with Jira, Smartsheet, monday.com, Excel trackers, SAP Cloud ALM and Microsoft Project for ERP programmes and PMOs.",
    path: "/compare",
  });
  const all = [...COMPARES.map((c) => [`/compare/${c.slug}`, c.name, c.summary]), ["/vs/microsoft-project", "Microsoft Project", "General-purpose scheduling compared with a purpose-built delivery operating system."]];
  return (
    <SeoShell>
      <SeoHero eyebrow="Compare" h1="How Capittance compares." intro="Honest, side-by-side comparisons for programme leaders choosing tools for ERP rollouts and portfolio governance." />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="compare-grid">
          {all.map(([to, name, sum]) => (
            <Link key={to} to={to} className="p-6 rounded-md bg-white border hover:-translate-y-0.5 transition-transform" style={{ borderColor: "#F0D3D8" }} data-testid={`compare-card-${to.split("/").pop()}`}>
              <div className="font-display text-lg font-extrabold text-[#0F1723]">Capittance vs {name}</div>
              <p className="text-sm text-zinc-600 mt-2 line-clamp-3">{sum}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: "#7A1F2B" }}>Read comparison <ArrowRight size={13}/></span>
            </Link>
          ))}
        </div>
        <PlanCTA id="compare-hub" />
      </main>
    </SeoShell>
  );
}
